import { useCallback, useMemo } from 'react';

import { InventoryItemCondition, Services } from '@ekhodealer/ekho-common';
import { validateReadyForOnlineSale } from '@ekhodealer/ekho-common';

import type { IInventoryItem, ServicesProviderInventory } from '@ekhodealer/ekho-common';
import type { UseQueryResult } from '@tanstack/react-query';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useEnableOnlineSalesMutation } from '../api/useEnableOnlineSales';

/**
 * Categorized inventory items for online sales operation
 */
export interface CategorizedOnlineSalesItems {
	/** Items ready to enable for online sales */
	ready: IInventoryItem[];
	/** Items that don't meet validation criteria */
	notEligible: Array<{
		item: IInventoryItem;
		reasons: string[];
	}>;
	/** Items already marked for online sale */
	alreadyEnabled: IInventoryItem[];
}

/**
 * Get validation failure reasons for an inventory item
 */
function getValidationFailureReasons(
	item: IInventoryItem,
	servicesProvider: ServicesProviderInventory,
): string[] {
	const reasons: string[] = [];
	const onlineSaleConfig = {
		legalAddress: servicesProvider?.legalAddress,
		config: servicesProvider?.config,
	};

	if (item.newUsed !== InventoryItemCondition.NEW) {
		reasons.push('Not a new vehicle');
	}

	if (item.matching?.status !== 'MATCHED') {
		reasons.push('Vehicle not fully matched');
	}

	if (!item.pricing?.retailPrice || item.pricing.retailPrice <= 0) {
		reasons.push('Missing or invalid retail price');
	}

	if (!item.vin || item.vin.length === 0) {
		reasons.push('Missing VIN');
	}

	if (!onlineSaleConfig?.config?.[Services.INVENTORY]?.isLiveForOnlineSales) {
		reasons.push('Dealership not configured for online sales');
	}

	return reasons;
}

/**
 * Hook for managing enable online sales operations on selected inventory items
 * Provides categorization and execution
 */
export const useEnableOnlineSalesSelection = () => {
	const enableMutation = useEnableOnlineSalesMutation();
	const { data: servicesProvider } = useCurrentServicesProvider() as UseQueryResult<
		ServicesProviderInventory,
		Error
	>;

	/**
	 * Categorize items into ready, notEligible, and alreadyEnabled groups
	 */
	const categorizeOnlineSalesItems = useCallback(
		(items: IInventoryItem[]): CategorizedOnlineSalesItems => {
			const result: CategorizedOnlineSalesItems = {
				ready: [],
				notEligible: [],
				alreadyEnabled: [],
			};

			if (!servicesProvider) {
				return result;
			}

			for (const item of items) {
				// Check if already enabled
				if (item.isMarkedForOnlineSale) {
					result.alreadyEnabled.push(item);
					continue;
				}

				// Validate if ready for online sales
				const isValid = validateReadyForOnlineSale(item, servicesProvider as ServicesProviderInventory);

				if (isValid) {
					result.ready.push(item);
				} else {
					const reasons = getValidationFailureReasons(item, servicesProvider);
					result.notEligible.push({ item, reasons });
				}
			}

			return result;
		},
		[servicesProvider],
	);

	/**
	 * Execute enable online sales for items
	 * Categorizes and enables ready items
	 * Returns the count of items that were enabled
	 */
	const enableItemsByIds = useCallback(
		async (itemIds: Set<string>, items: IInventoryItem[]): Promise<number> => {
			if (!servicesProvider || itemIds.size === 0 || items.length === 0) {
				return 0;
			}

			// Categorize items
			const categorized = categorizeOnlineSalesItems(items);
			const readyItems = categorized.ready;

			if (readyItems.length === 0) {
				return 0;
			}

			const inventoryIds = readyItems.map((item) => item.id);

			await enableMutation.mutateAsync({
				servicesProviderId: servicesProvider.id,
				inventoryIds,
			});

			return readyItems.length;
		},
		[categorizeOnlineSalesItems, enableMutation, servicesProvider],
	);

	return useMemo(
		() => ({
			enableItemsByIds,
			isPending: enableMutation.isPending,
		}),
		[enableItemsByIds, enableMutation.isPending],
	);
};
